import React from 'react';
import { Navigate } from 'react-router-dom';
import { defaultRouteForRole } from '../utils/navigation';

const C = {
  bg: '#070B14',
  text: '#F8FAFC',
  muted: '#94A3B8',
  accent: '#22D3EE',
  accent2: '#2563EB',
  card: '#0F172A',
  border: '#1E293B',
};

function HeroButton({ href, text, primary = true }) {
  return (
    <a
      href={href}
      style={{
        display: 'inline-block',
        padding: '13px 22px',
        borderRadius: 10,
        fontWeight: 700,
        textDecoration: 'none',
        border: primary ? 'none' : `1px solid ${C.accent}`,
        background: primary ? `linear-gradient(90deg, ${C.accent}, ${C.accent2})` : 'transparent',
        color: primary ? '#021220' : C.accent,
      }}
    >
      {text}
    </a>
  );
}

function Stat({ value, label }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: C.text }}>{value}</div>
      <div style={{ fontSize: 13, color: C.muted }}>{label}</div>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20 }}>
      <div style={{ fontSize: 22, marginBottom: 10 }}>{icon}</div>
      <div style={{ color: C.text, fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.5 }}>{desc}</div>
    </div>
  );
}

export default function Landing() {
  const token = localStorage.getItem('access_token');
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  if (token) {
    return <Navigate to={defaultRouteForRole(user?.role)} replace />;
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <div style={{ borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, backdropFilter: 'blur(8px)', background: '#070B14DD', zIndex: 50 }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <img src="/yupay-logo.svg" alt="Yupay Fintech" style={{ height: 40 }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <HeroButton href="#beneficios" text="Ver beneficios" primary={false} />
            <HeroButton href="/login" text="Ingresar" />
          </div>
        </div>
      </div>

      <section style={{
        background: `linear-gradient(120deg, rgba(2,6,23,0.88), rgba(14,116,144,0.45)), url('https://images.unsplash.com/photo-1559526324-593bc073d938?auto=format&fit=crop&w=1600&q=80') center/cover no-repeat`,
      }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '72px 20px 84px' }}>
          <div style={{ maxWidth: 720 }}>
            <div style={{ display: 'inline-block', background: '#22D3EE1A', border: '1px solid #22D3EE66', color: C.accent, padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 700, marginBottom: 18 }}>
              FINTECH OPERATIVA PARA COOPERATIVAS Y COMERCIOS
            </div>
            <h1 style={{ fontSize: 'clamp(32px, 5vw, 56px)', lineHeight: 1.07, margin: '0 0 16px', fontWeight: 800 }}>
              Convierte procesos lentos en
              <span style={{ color: C.accent }}> crecimiento medible</span>
            </h1>
            <p style={{ color: '#E2E8F0', fontSize: 19, lineHeight: 1.55, maxWidth: 680 }}>
              Yupay Fintech integra onboarding, cuentas, pagos multigateway, conciliación y compliance en una sola plataforma para operar más rápido, reducir errores y escalar con control.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 26 }}>
              <HeroButton href="/login" text="Entrar al panel" />
              <HeroButton href="#problema" text="Ver cómo lo resolvemos" primary={false} />
            </div>
          </div>
        </div>
      </section>

      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 20px 10px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 16, background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20 }}>
          <Stat value="99.9%" label="Disponibilidad objetivo" />
          <Stat value="24/7" label="Operación digital" />
          <Stat value="+4" label="Proveedores de pago" />
          <Stat value="End-to-End" label="Trazabilidad operativa" />
        </div>
      </section>

      <section id="problema" style={{ maxWidth: 1100, margin: '0 auto', padding: '56px 20px 10px' }}>
        <h2 style={{ fontSize: 34, marginBottom: 14, fontWeight: 800 }}>La problemática real del día a día</h2>
        <p style={{ color: C.muted, marginBottom: 22 }}>Cuando la operación depende de procesos manuales y sistemas aislados, crece el riesgo y baja la velocidad comercial.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 14 }}>
          <Feature icon="⚠️" title="Retrabajo y errores manuales" desc="Procesos operativos sin automatización provocan inconsistencias y pérdidas de tiempo." />
          <Feature icon="🧩" title="Integraciones dispersas" desc="Cada proveedor de pago funciona con lógica distinta y complica control y soporte." />
          <Feature icon="📉" title="Poca visibilidad ejecutiva" desc="Sin métricas claras es difícil decidir, priorizar y anticipar riesgos operativos." />
        </div>
      </section>

      <section id="beneficios" style={{ maxWidth: 1100, margin: '0 auto', padding: '56px 20px 0' }}>
        <h2 style={{ fontSize: 34, marginBottom: 14, fontWeight: 800 }}>¿Qué ganas con Yupay Fintech?</h2>
        <p style={{ color: C.muted, marginBottom: 22 }}>Una plataforma comercial y operativa para crecer sin perder control.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))', gap: 14 }}>
          <Feature icon="💳" title="Cobros más inteligentes" desc="Authorize, capture y refund con configuración por comercio y por tenant." />
          <Feature icon="🛡️" title="Seguridad y compliance" desc="Trazabilidad, controles por rol, validación de webhooks y evidencia para auditoría." />
          <Feature icon="📊" title="SLA y performance" desc="Métricas por proveedor para decidir rápido y mejorar disponibilidad comercial." />
          <Feature icon="🚀" title="Escalamiento controlado" desc="Arquitectura lista para crecer en volumen, productos y canales digitales." />
        </div>
      </section>

      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '56px 20px 70px' }}>
        <div style={{
          background: 'linear-gradient(135deg, #0EA5E9, #2563EB)',
          borderRadius: 18,
          padding: '36px 28px',
          textAlign: 'center',
          boxShadow: '0 14px 40px rgba(14,165,233,0.28)'
        }}>
          <h3 style={{ fontSize: 32, margin: 0, marginBottom: 10, fontWeight: 800 }}>Haz que tu operación financiera venda más y falle menos</h3>
          <p style={{ fontSize: 17, color: '#E0F2FE', marginBottom: 20 }}>Empieza con Yupay Fintech y centraliza lo crítico de tu negocio en una sola plataforma.</p>
          <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 12 }}>
            <HeroButton href="/login" text="Ir al login" />
            <a href="https://wa.me/593000000000?text=Hola%20Yupay%20Fintech%2C%20quiero%20una%20demo" target="_blank" rel="noreferrer" style={{
              display: 'inline-block',
              padding: '13px 22px',
              borderRadius: 10,
              fontWeight: 700,
              textDecoration: 'none',
              border: '1px solid #CFFAFE',
              color: '#E0F2FE'
            }}>Solicitar demo</a>
          </div>
        </div>
      </section>
    </div>
  );
}
